import { z } from 'zod';
import { TicketPriority, TicketStatus } from '../types/index.js';

// ─────────────────────────────────────────────
// Shared / Primitives
// ─────────────────────────────────────────────

const LinkedItemSchema = z.object({
  id: z.uuid(),
  type: z.enum(['asset', 'premise']),
});

// TODO: Make optional if linking mode not 'adhoc'
const AddressSchema = z.object({
  address1: z.string().min(1, 'Address is required').optional(),
  address2: z.string().nullable().optional(),
  city: z.string().min(1, 'City is required').optional(),
  state: z.string().nullable().optional(),
  country: z.string().min(1, 'Country is required').optional(),
  zipcode: z.string().nullable().optional(),
  lat: z.number({ error: 'Latitude is required' }).optional(),
  lon: z.number({ error: 'Longitude is required' }).optional(),
});

// ─────────────────────────────────────────────
// Create Ticket
// ─────────────────────────────────────────────

export const CreateTicketSchema = AddressSchema.extend({
  priority: z.enum(TicketPriority),
  status: z.enum(TicketStatus),
  projectId: z.uuid(),
  description: z.string().optional(),
  skillsRequired: z.array(z.number().int().positive()).optional(),
  startDate: z.string().nullable().optional(),
  crewId: z.uuid().nullable().optional(),
  isAuto: z.boolean(),
  endDate: z.string().nullable().optional(),
  // Typed as unknown for now — tighten this once your custom fields shape stabilises
  customFields: z.record(z.string(), z.unknown()).nullable().optional(),
  linkedItems: z.array(LinkedItemSchema).nullable().optional(),
  linkingMode: z.enum(['adhoc', 'multi', 'single']),
});

export type CreateTicketDto = z.infer<typeof CreateTicketSchema>;

// ─────────────────────────────────────────────
// Update Ticket
// ─────────────────────────────────────────────

export const UpdateTicketSchema = CreateTicketSchema.omit({
  projectId: true, // project reassignment should be its own operation
  isAuto: true, // dispatch mode shouldn't change post-creation
  linkingMode: true,
})
  .partial()
  .extend({
    ticketId: z.uuid(),
    // Fields that are only valid on update
    resolvedAt: z.iso.datetime({ offset: true }).nullable().optional(),
  });

export interface CompleteTicketDto {
  ticketId: string;
  resolutionSummary?: string;
  completionChecklist?: Record<string, boolean>;
}

export type UpdateTicketDto = z.infer<typeof UpdateTicketSchema>;

// ─────────────────────────────────────────────
// Query / Filter
// ─────────────────────────────────────────────

// Zod schema for controller-level validation
export const TicketQuerySchema = z.object({
  q: z.string().optional(),
  status: z.string().optional(),
  priority: z.string().optional(),
  projectId: z.uuid().optional(),
  crewId: z.uuid().optional(),
  createdOn: z.string().optional(),
  category: z.string().optional(),
  assignmentStatus: z.string().optional(),
  assignedTo: z.string().optional(),
  scope: z.string().optional(),
  page: z.coerce.number().int().positive().default(1),
  limit: z.coerce.number().int().positive().max(100).default(20),
});

export interface PaginatedResult<T> {
  data: T[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
  hasNext: boolean;
  hasPrev: boolean;
}

export type TicketQueryDto = z.infer<typeof TicketQuerySchema>;
// ─────────────────────────────────────────────
// Re-export shared primitives for reuse
// ─────────────────────────────────────────────

export type LinkedItem = z.infer<typeof LinkedItemSchema>;
export type AddressDto = z.infer<typeof AddressSchema>;
